import { createSignal } from "solid-js";
import { useWs } from "@/shared/ws";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useNotify } from "@/shared/notify";
import type { PanelStatus } from "@/shared/panel-base";

type QueryLike = () => { isFetching: boolean; error: unknown };

export function usePanel(...queries: QueryLike[]) {
  const ws = useWs();
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus()!;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    for (const q of queries) {
      const s = q();
      if (s.isFetching) return { text: "Chargement...", level: "info" };
      if (s.error) return { text: `Erreur: ${String(s.error)}`, level: "danger" };
    }
    return undefined;
  };

  return {
    ws,
    enabled,
    status,
    setActionStatus,
    tools: useToolClient(),
    confirm: useConfirm(),
  };
}

export function usePanelAction(setActionStatus: (s: PanelStatus | null) => void, refetch: () => Promise<unknown>) {
  const tools = useToolClient();
  const confirm = useConfirm();
  const notify = useNotify();

  return async function run<R = unknown>(
    tool: string,
    params: Record<string, unknown>,
    opts: {
      confirmTitle: string;
      confirmBody: string;
      level?: "warn" | "danger";
      entityName?: string;
      /** Texte court affiché dans le toast à la fin (success). Défaut :
       *  l'entityName ou le nom du tool. Passer `null` pour ne pas notifier. */
      successToast?: string | null;
    },
  ): Promise<R | null> {
    const ok = await confirm.confirm({
      title: opts.confirmTitle,
      body: opts.confirmBody,
      level: opts.level ?? "warn",
      entityName: opts.entityName,
      confirmLabel: "Confirmer",
    });
    if (!ok) return null;
    setActionStatus({ text: "Exécution...", level: "info" });
    try {
      const result = await tools.call<R>(tool, params, {
        userConfirmedAt: new Date().toISOString(),
      });
      setActionStatus(null);
      await refetch();
      // Toast de succès — globalement visible même si l'opérateur a quitté
      // le panel entre-temps. Désactivable via successToast: null.
      if (opts.successToast !== null) {
        const label = opts.successToast ?? opts.entityName ?? tool;
        notify.ok(`${opts.confirmTitle} — terminé`, { hint: label, durationMs: 3500 });
      }
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionStatus({ text: `Erreur: ${msg}`, level: "danger" });
      notify.danger(`${opts.confirmTitle} — échec`, {
        hint: msg.length > 120 ? msg.slice(0, 120) + "…" : msg,
        durationMs: 6000,
      });
      return null;
    }
  };
}
